import { Column, DataTable, Row, Text, Title } from '@ydistri/ds';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { PickingProgressInfoData } from './PickingProgressInfoTableTypes';
import { InfoIconTooltip } from '../../../../../../../components/icons/InfoIconTooltip';
import { computeRemSize } from '@ydistri/theme';

interface InfoTableCellProps {
  value: string | number;
  highlight?: boolean;
  description?: string;
}

const InfoTableCell: React.FC<InfoTableCellProps> = ({ value, description, highlight = false }) => {
  const valueComponent = (
    <Text $bold={highlight} $size="small">
      {value}
    </Text>
  );
  if (!description) {
    return valueComponent;
  } else {
    return (
      <Row $gap="1rem" $alignItems="center">
        {valueComponent}
        <InfoIconTooltip content={description} />
      </Row>
    );
  }
};

interface PickingProgressInfoTableProps {
  id: string;
  title?: ReactNode;
  data: PickingProgressInfoData[];
}

const PickingProgressInfoTable: React.FC<PickingProgressInfoTableProps> = ({ id, title, data }) => {
  const tableColumns = useMemo((): ColumnsType<PickingProgressInfoData> => {
    return [
      {
        title: 'Label',
        dataIndex: 'label',
        width: computeRemSize(160),
        render: (value: string | number, record: PickingProgressInfoData) => {
          return (
            <InfoTableCell
              value={value}
              highlight={record.isTotal}
              description={record.description}
            />
          );
        },
      },
      {
        title: 'Value',
        dataIndex: 'value',
        align: 'right',
        render: (value: string | number, record) => {
          return <InfoTableCell value={value} highlight={record.isTotal} />;
        },
      },
    ];
  }, []);

  const titleComponent = useMemo(() => {
    if (typeof title === 'string') {
      return (
        <Row $minHeight="2rem">
          <Title $size="small">{title}</Title>
        </Row>
      );
    } else {
      return title;
    }
  }, [title]);

  const rowKeyProvider = useCallback((record: PickingProgressInfoData) => record.label, []);

  return (
    <Column $gap="8px">
      {titleComponent}
      <DataTable<PickingProgressInfoData>
        id={id}
        dataSource={data}
        columns={tableColumns}
        size="small"
        rowKey={rowKeyProvider}
      />
    </Column>
  );
};

export default PickingProgressInfoTable;
